import { useGlobalSearchParams, router } from 'expo-router';
import { View, Text, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react-native';
import { supabase, isShortId, toError } from '../../lib/supabase';
import { resolvePaperByShortId } from '../../lib/shortIdResolver';
import { PaperData, ProjectPaperContext } from './types';
import PaperDetailView from './PaperDetailView';
import MetaTags from '../MetaTags';
import Header from '../Header';
import Footer from '../Footer';
import SharePaperModal from './SharePaperModal';
import colors from '../../lib/colors';
import { createSlug, getShortId, sanitizeStandalonePaperSearch } from '../../lib/paperUrl';
import { normalizePaperData } from '../../lib/paperAuthors';
import { usePaperSummary } from '../../hooks/usePaperSummary';
import { useStandaloneProjectPaperContext } from '../../hooks/useStandaloneProjectPaperContext';
import { useProjectPaperFeedbackBindings } from '../../hooks/useProjectPaperFeedbackBindings';
import { useSession } from '../../lib/SessionContext';
import type { PaperPageState } from './types';
import { canAccessMainApp } from '../../utils/authHelpers';
import {
    canGenerateUnscopedPaperSummary,
    isSummaryEligibleProjectPaper,
    resolvePaperSummaryProjectId,
} from '../../lib/paperSummaryAccess';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { type LocalizedMessage, useOptionalResolvedMessage } from '../../lib/i18n/messages';
import { localizedPublicUrl, stripStaticPublicExportPrefix } from '../../lib/i18n/publicWeb';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';

interface StandalonePaperPageContentProps {
    paperId: string;
    urlSlug?: string | null;
}

function normalizePathname(pathname: string): string {
    const normalized = pathname
        .replace(/\/+/g, '/')
        .replace(/\/+$/, '');
    return normalized || '/';
}

export default function StandalonePaperPageContent({
    paperId,
    urlSlug = null,
}: StandalonePaperPageContentProps) {
    const { messageLocale } = useAppLocale();
    const {
        backHref,
        backText,
        share,
        projectId,
        projectPaperId,
    } = useGlobalSearchParams<{
        backHref?: string | string[];
        backText?: string | string[];
        share?: string | string[];
        projectId?: string | string[];
        projectPaperId?: string | string[];
    }>();
    const resolvedBackHref = Array.isArray(backHref) ? backHref[0] : backHref;
    const resolvedBackText = Array.isArray(backText) ? backText[0] : backText;
    const resolvedShare = Array.isArray(share) ? share[0] : share;
    const resolvedProjectId = Array.isArray(projectId) ? projectId[0] : projectId;
    const resolvedProjectPaperId = Array.isArray(projectPaperId) ? projectPaperId[0] : projectPaperId;
    const initialShowShareModal = resolvedShare === '1';
    const hasMeaningfulBackHistory = Boolean(
        resolvedBackHref
        && resolvedBackHref.startsWith('/')
        && !resolvedBackHref.startsWith('//')
    );

    const [paper, setPaper] = useState<PaperData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const resolvedError = useOptionalResolvedMessage(error);
    const [showShareModal, setShowShareModal] = useState(false);
    const lastCanonicalRedirect = useRef<string | null>(null);
    const { session } = useSession();

    const {
        projectPaperContext,
        setProjectPaperContext,
    } = useStandaloneProjectPaperContext({
        paperId: paper?.id || null,
        projectId: resolvedProjectId,
        projectPaperId: resolvedProjectPaperId,
        surface: 'standalone_paper',
    });
    const viewerOwnsProject = Boolean(
        projectPaperContext?.projectOwnerId
        && session?.user?.id
        && projectPaperContext.projectOwnerId === session.user.id
    );
    const canGenerateUnscopedSummary = canGenerateUnscopedPaperSummary(session?.user?.id);

    const {
        summary,
        job: summaryJob,
        status: summaryStatus,
        error: summaryError,
        summaryLimitReached,
        generateSummary,
        retry: retrySummary,
    } = usePaperSummary(
        paper?.id || null,
        resolvePaperSummaryProjectId({
            canGenerateUnscoped: canGenerateUnscopedSummary,
            viewerOwnsProject,
            projectId: projectPaperContext?.projectId,
        })
    );

    const {
        onSetFeedback,
        onClearFeedback,
        feedbackPending,
    } = useProjectPaperFeedbackBindings({
        projectId: projectPaperContext?.projectId,
        projectPaperId: projectPaperContext?.projectPaperId,
        feedback: projectPaperContext?.feedback || null,
        surface: 'standalone_paper',
        onOptimisticChange: useCallback((nextFeedback) => {
            setProjectPaperContext((current: ProjectPaperContext | null) => (
                current ? { ...current, feedback: nextFeedback } : current
            ));
        }, [setProjectPaperContext]),
    });

    useEffect(() => {
        if (paperId) {
            void fetchPaperBasicInfo();
        }
    }, [paperId, urlSlug]);

    const fetchPaperBasicInfo = async () => {
        setLoading(true);
        setError(null);

        const selectFields = `
            id,
            short_id,
            slug,
            title,
            abstract,
            authors,
            published_date,
            pdf_url,
            pdf_availability_status,
            pdf_availability_source,
            pdf_availability_license,
            pdf_availability_checked_at,
            pdf_availability_next_check_at,
            url,
            doi,
            source_id,
            source_paper_id,
            source_paper_version
        `;

        try {
            let data: any = null;
            let fetchError: any = null;

            if (isShortId(paperId)) {
                const result = await resolvePaperByShortId(paperId, urlSlug, selectFields);
                data = result.data;
                fetchError = result.error;
            } else {
                const result = await supabase
                    .from('paper')
                    .select(selectFields)
                    .eq('id', paperId)
                    .single();
                data = result.data;
                fetchError = result.error;
            }

            if (fetchError) {
                if (fetchError.message?.includes('JSON object requested')
                    || fetchError.message?.includes('no rows returned')) {
                    Sentry.captureMessage('Paper not found', {
                        level: 'info',
                        extra: { paperId, urlSlug, route: '/p/[paperId]' },
                    });
                    router.replace('/+not-found');
                    return;
                }
                throw toError(fetchError);
            }

            if (!data) {
                Sentry.captureMessage('Paper not found (null data)', {
                    level: 'info',
                    extra: { paperId, urlSlug, route: '/p/[paperId]' },
                });
                router.replace('/+not-found');
                return;
            }

            setPaper(normalizePaperData(data) as PaperData);
        } catch (err: any) {
            console.error('Failed to fetch paper:', err);
            Sentry.captureException(err, {
                extra: { paperId, urlSlug, route: '/p/[paperId]' },
            });
            console.error('[StandalonePaper] Failed to load paper:', err);
            setError(msg({ id: 'paper.loadFailure', message: 'Failed to load paper' }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!paper || Platform.OS !== 'web') {
            return;
        }

        const paperShortId = paper.short_id || getShortId(paper.id);
        const paperSlug = paper.slug || createSlug(paper.title);
        const canonicalPath = `/p/${paperShortId}/${paperSlug}`;

        if (typeof window !== 'undefined') {
            const currentPath = stripStaticPublicExportPrefix(window.location.pathname);
            const currentSearch = window.location.search;
            const sanitizedSearch = sanitizeStandalonePaperSearch(currentSearch);
            const currentSearchParams = new URLSearchParams(
                currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch
            );
            const hasLeakedRouteParams =
                currentSearchParams.has('paperId') || currentSearchParams.has('title');
            const normalizedCurrentPath = normalizePathname(currentPath);
            const normalizedCanonicalPath = normalizePathname(canonicalPath);
            const isAlreadyCanonical = normalizedCurrentPath === normalizedCanonicalPath;
            const redirectTarget = `${canonicalPath}${sanitizedSearch}`;

            if (isAlreadyCanonical && !hasLeakedRouteParams) {
                lastCanonicalRedirect.current = null;
                return;
            }

            if (lastCanonicalRedirect.current === redirectTarget) {
                return;
            }

            lastCanonicalRedirect.current = redirectTarget;
            router.replace(redirectTarget);
        }
    }, [paper]);

    useEffect(() => {
        if (initialShowShareModal && paper) {
            setShowShareModal(true);
        }
    }, [initialShowShareModal, paper]);

    const handleSharePress = () => {
        setShowShareModal(true);
    };

    const handleCloseShareModal = () => {
        setShowShareModal(false);

        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.delete('share');
            const nextSearch = url.searchParams.toString();
            const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
            window.history.replaceState({}, '', nextUrl);
        }
    };

    if (loading) {
        return (
            <View style={styles.container}>
                <Header />
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={colors.primaryGreen} />
                    <Text style={styles.loadingText}><Trans id="papers.standalonepaperpagecontent.loadingPaper" comment="UI copy in components/papers/StandalonePaperPageContent.tsx">Loading paper...</Trans></Text>
                </View>
                <Footer />
            </View>
        );
    }

    if (error || !paper) {
        return (
            <View style={styles.container}>
                <Header />
                <View style={styles.errorContainer}>
                    <Text style={styles.errorTitle}><Trans id="papers.standalonepaperpagecontent.paperNotFound" comment="UI copy in components/papers/StandalonePaperPageContent.tsx">Paper Not Found</Trans></Text>
                    <Text style={styles.errorText}>
                        {resolvedError || <Trans id="papers.standalonepaperpagecontent.thePaperYouAreLookingForCouldNotBeFound" comment="Conditional UI copy in components/papers/StandalonePaperPageContent.tsx">The paper you are looking for could not be found.</Trans>}
                    </Text>
                </View>
                <Footer />
            </View>
        );
    }

    const canonicalPaperPath = `/p/${paper.short_id || getShortId(paper.id)}/${paper.slug || createSlug(paper.title)}`;
    const shareUrl = localizedPublicUrl(canonicalPaperPath, messageLocale, {
        allowTestLocale: messageLocale === 'en-XA',
    });
    const isAuthenticatedViewer = canAccessMainApp(session);
    const viewer = viewerOwnsProject
        ? 'owner'
        : isAuthenticatedViewer
            ? 'non_owner'
            : 'anonymous';
    const canFeedback = Boolean(projectPaperContext && viewer === 'owner');
    const canGenerateScopedSummary = canFeedback && isSummaryEligibleProjectPaper({
        relevanceClass: projectPaperContext?.relevanceClass,
        processingState: projectPaperContext?.processingState,
    });
    const canGenerateSummary = canGenerateScopedSummary || canGenerateUnscopedSummary;
    const pageState: PaperPageState = {
        surface: 'standalone',
        viewer,
        access: 'accessible',
        canShare: true,
        canFeedback,
        hasRecommendationContext: Boolean(projectPaperContext),
        summaryState: summaryStatus,
        primaryCta: canGenerateSummary && summaryStatus === 'not_requested'
            ? 'generate_summary'
            : canGenerateSummary && summaryStatus === 'failed'
                ? 'retry_summary'
                : summaryStatus === 'not_requested' || summaryStatus === 'failed'
                    ? isAuthenticatedViewer
                        ? 'create_digest'
                        : 'sign_up'
                    : 'none',
        templateProjectId: null,
    };

    return (
        <>
            <MetaTags
                surfaceId="paper-short"
                canonicalPathname={canonicalPaperPath}
                title={{
                    ...msg({
                        id: 'seo.paper.title',
                        message: '{paperTitle} - Paperzilla',
                        comment: 'Public paper metadata title. The paper title is scientific content and must remain unchanged.',
                    }),
                    values: { paperTitle: paper.title },
                }}
                description={paper.abstract?.substring(0, 160) || msg({
                    id: 'seo.paper.sourceFallbackDescription',
                    message: 'Research paper from arXiv, medRxiv, bioRxiv, ChemRxiv, ChinaXiv, IACR ePrint, or PubMed',
                    comment: 'Fallback metadata description when a public paper has no abstract. Preserve source names.',
                })}
                type="article"
            />

            <PaperDetailView
                paper={paper}
                pageState={pageState}
                projectPaperContext={projectPaperContext || undefined}
                summary={summary}
                summaryJob={summaryJob}
                summaryStatus={summaryStatus}
                summaryError={summaryError}
                summaryLimitReached={summaryLimitReached}
                onGenerateSummary={generateSummary}
                onRetry={retrySummary}
                showBackLink={hasMeaningfulBackHistory}
                backLinkHref={resolvedBackHref}
                backLinkText={resolvedBackText || '← Back'}
                onSharePress={handleSharePress}
                feedbackPending={feedbackPending}
                onSetProjectFeedback={canFeedback ? onSetFeedback : undefined}
                onClearProjectFeedback={canFeedback ? onClearFeedback : undefined}
            />

            <SharePaperModal
                visible={showShareModal}
                onClose={handleCloseShareModal}
                paperTitle={paper.title}
                shareUrl={shareUrl}
                projectId=""
                isOwner={false}
                isPublic={true}
            />
        </>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 60,
        ...(Platform.OS === 'web' && {
            maxWidth: 900,
            alignSelf: 'center',
            width: '100%',
            marginHorizontal: 'auto',
        }),
    },
    loadingText: {
        marginTop: 16,
        fontSize: 16,
        color: colors.text700,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 40,
        paddingVertical: 60,
        ...(Platform.OS === 'web' && {
            maxWidth: 900,
            alignSelf: 'center',
            width: '100%',
            marginHorizontal: 'auto',
        }),
    },
    errorTitle: {
        fontSize: 24,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 12,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 16,
        color: colors.text700,
        textAlign: 'center',
        lineHeight: 24,
    },
});
